/**
 * **Area 右键菜单**（也用于 Area 头部的「⋯」按钮）—— 从
 * `composables/useMenus.ts` 向下拆出来的子模块。
 *
 * ## 为什么单独一个文件
 *
 * `useMenus.ts` 原本是**一个 1160 行的函数**，里面塞了三套完全不同的菜单：
 * Area 右键 / 选中节点右键 / 顶部菜单栏。三者的耦合度实测极低 ——
 * `areaSections` 只用得到 `MenusCtx` 那 67 个字段里的 **6 个**，
 * 而且和另外两套的字段**交集为 0**。
 *
 * 所以按**耦合**拆（而不是按行数）：这里只声明它真正需要的那几个能力，
 * 上游（`useMenus`）负责把宿主那 67 个字段喂进来。
 *
 * ⚠️ **函数体逐字不动**，只把原来从闭包读的名字换成 `ctx.xxx()`
 * （`layout` 是 ref，所以是 `ctx.layout()`）。
 * 这套菜单的回归断言在 `mount-app.ts`（真 DOM 打开它数结构），
 * 所以搬家有没有搬坏，跑 `npm run check` 就知道。
 *
 * ## 归属
 *
 * **向下**：`useMenus.ts` → `menus/areaSections.ts`。
 * 只有 `useMenus.ts` 引用它，不是平级同级模块。
 */
import { EDITOR_META, REGION_LABELS, areasOf, editorOf, findArea, panelOfSide } from '@/layout/areas'
import { pruneMenu } from '@/types/menu'
import type { MenuSection } from '@/types/menu'
import type { EditorType, LayoutState, RegionKey } from '@/layout/areas'

/**
 * 这套菜单需要宿主提供的能力。
 *
 * ⚠️ `layout` 传**函数**（取当前值），不是 ref —— 上游那份是 computed，
 * 菜单在打开那一刻才读它。
 */
export interface AreaMenuCtx {
  layout: () => LayoutState
  swapAreas: (a: string, b: string) => void
  toggleRegionOf: (areaId: string, key: RegionKey) => void
  splitAreaOf: (areaId: string, dir: 'row' | 'col') => void
  toggleMaximize: (areaId: string) => void
  closeArea: (areaId: string) => void
  /** 设某一侧的面板（`null` = 收起）——「⋯」菜单里的面板开关用它 */
  setPanelOfSide: (areaId: string, side: 'left' | 'right', tab: string | null) => void
}

/** 给我一个 areaId，给你这套菜单 */
export function areaSections(ctx: AreaMenuCtx, areaId: string): MenuSection[] {
  const area = findArea(ctx.layout().root, areaId)
  if (!area) return []

  const others = areasOf(ctx.layout().root).filter((item) => item.id !== areaId)
  /* ⚠️ 菜单说的是**这个窗口现在显示什么**（`editorOf`），不是它"要"显示什么 */
  const shown = editorOf(area)

  return pruneMenu([
    {
      id: 'area-swap-section',
      // 标题就是这个 Area 当前在显示什么 —— 用户一眼知道「我右键的是哪个」
      title: `${EDITOR_META[shown].label}（${area.id}）`,
      titleStyle: 'divider',
      items: [
        {
          id: 'area-swap',
          label: '与另一个区域交换内容',
          /*
           * ⚠️ 少于两个 Area 时这一项**不出现**（而不是灰着）。
           * 只有一个 Area 的时候「交换」是个不存在的概念，
           * 摆一个永远点不动的按钮只会让人试图去理解它。
           */
          children: others.length
            ? [
                {
                  id: 'area-swap-list',
                  title: '交换成',
                  titleStyle: 'divider' as const,
                  items: others.map((other) => ({
                    id: `area-swap-${other.id}`,
                    label: `${EDITOR_META[editorOf(other)].label}（${other.id}）`,
                    run: () => ctx.swapAreas(areaId, other.id),
                  })),
                },
              ]
            : [],
        },
      ].filter((item) => !item.children || item.children.length > 0),
    },
    /*
     * 「⋯」按钮弹出的就是**这一套**菜单（和右键 Area 头同一个）。
     *
     * ⚠️ 所以这里要包含**头部那一排按钮的全部动作** —— 区域开关、
     * 左右/上下分屏、最大化、关闭。分开写两份的话，窄屏收起之后
     * 用户就再也够不到「切换小地图」这类开关了。
     */
    {
      id: 'area-regions',
      title: '显示',
      titleStyle: 'divider',
      items: [
        /*
         * ⚠️ **「标题栏」不进菜单**（真机反馈："去掉「标题栏」菜单项"）。
         *
         * 它是一把**自锁的锁**：菜单本身就在标题栏上，把标题栏关掉之后
         * 那个「⋯」也跟着没了 —— 用户再也点不回来（只能去改布局文件）。
         * 真要藏标题栏，那是工作区/预设该干的事，不是一个下拉菜单的开关。
         */
        ...EDITOR_META[shown].regions
          .filter((key) => key !== 'header')
          .map((key) => ({
            id: `area-region-${key}`,
            label: REGION_LABELS[key] ?? key,
            checked: area.regions[key],
            keywords: ['区域', 'region', '显示', '隐藏', key],
            run: () => ctx.toggleRegionOf(areaId, key),
          })),
        /*
         * ★ **左右面板开关并进菜单**（真机反馈："包括左面板、右面板的按钮，
         * 融合到「三个点」的菜单按钮里"）。
         *
         * ⚠️ 只在编辑器**声明了那一侧**时才出现（摆一个点了没反应的项比
         * 没有更糟）；状态读的是布局里的 `panels`，和组件那边是同一份。
         */
        ...(['left', 'right'] as const)
          .filter((side) => (EDITOR_META[shown].panels?.[side]?.length ?? 0) > 0)
          .map((side) => {
            const tabs = EDITOR_META[shown].panels![side]!
            const openTab = panelOfSide(area, side)
            return {
              id: `area-panel-${side}`,
              label: `${side === 'left' ? '左侧' : '右侧'}面板${openTab ? `（${tabs.find((t) => t.id === openTab)?.label ?? openTab}）` : ''}`,
              checked: Boolean(openTab),
              keywords: ['面板', 'panel', '侧栏', side === 'left' ? '左' : '右'],
              run: () => ctx.setPanelOfSide(areaId, side, openTab ? null : tabs[0]!.id),
            }
          }),
      ],
    },
    {
      id: 'area-layout',
      title: '分屏',
      titleStyle: 'divider',
      items: [
        {
          id: 'area-split-row',
          label: '左右分屏',
          run: () => ctx.splitAreaOf(areaId, 'row'),
        },
        {
          id: 'area-split-col',
          label: '上下分屏',
          run: () => ctx.splitAreaOf(areaId, 'col'),
        },
      ],
    },
    {
      id: 'area-actions',
      title: '这个区域',
      titleStyle: 'divider',
      items: [
        {
          id: 'area-maximize',
          label: ctx.layout().maximized === areaId ? '还原这个区域' : '最大化这个区域',
          run: () => ctx.toggleMaximize(areaId),
        },
        {
          id: 'area-close',
          label: '关闭这个区域',
          hint: areasOf(ctx.layout().root).length <= 1 ? '至少留一个' : undefined,
          danger: true,
          /* 最后一个 Area 关不掉（`closeArea` 里也是 no-op），所以直接禁用 */
          disabled: areasOf(ctx.layout().root).length <= 1,
          run: () => ctx.closeArea(areaId),
        },
      ],
    },
  ])
}

